import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { KnownPlatform } from '@root/entities/network'
import { KnownPlatformIcon } from '@root/entities/network/ui/known-platform/known-platform-icon'
import { useFormatDuration } from '@root/shared'
import { useTranslation } from '@root/shared/localization'

import { StatsLabel, StatsPanel } from './dev-tokens-common'
import { DevTokenWalletInfo } from './dev-tokens-wallet-info'

import type { PairModel } from '@root/entities/pair'

type Props = {
  pair: PairModel
}

export const DevTokensDevInfo: React.FC<Props> = ({ pair }) => {
  const { t } = useTranslation()
  const [txCreator] = useAtom(pair.initTxCreator)
  const [price] = useAtom(pair.price)
  const { formatDuration } = useFormatDuration()

  const knownPlatform = useMemo(() => {
    return txCreator?.funding?.by
      ? KnownPlatform.tryGetKnownPlatform(txCreator.funding.by)
      : undefined
  }, [txCreator?.funding?.by])

  const knownPlatformLabel = useMemo(() => {
    return knownPlatform ? KnownPlatform.labelOf(knownPlatform) : undefined
  }, [knownPlatform])

  const devBalanceInNative = useMemo(() => {
    if (!txCreator?.balanceInToken) {
      return undefined
    }
    return txCreator.balanceInToken.multipliedBy(price.priceNative)
  }, [txCreator?.balanceInToken, price.priceNative])

  const fundingAgo = useMemo(() => {
    if (!txCreator?.funding?.at) {
      return undefined
    }
    return formatDuration({
      startAt: txCreator.funding.at,
      mode: 'singleUnitShort',
    })
  }, [txCreator?.funding?.at, formatDuration])

  if (!txCreator) {
    return undefined
  }

  return (
    <Root>
      <StatsLabel>{t('dev-tokens.token-stats')}:</StatsLabel>
      <Row>
        <Key>{t('dev-tokens.dev')}:</Key>
        <DevTokenWalletInfo
          walletAddress={txCreator.address}
          network={pair.network}
          amount={devBalanceInNative}
        />
      </Row>
      {txCreator.funding ? (
        <Row>
          <Key>{t('common.funding')}:</Key>
          <ValueContainer>
            {knownPlatformLabel ? (
              <PlatformLabel>
                <div>{knownPlatformLabel}</div>
                <KnownPlatformIcon size={14} address={txCreator.funding.by} />
              </PlatformLabel>
            ) : (
              <DevTokenWalletInfo
                walletAddress={txCreator.funding.by}
                network={pair.network}
                amount={txCreator.funding.amount}
              />
            )}

            {fundingAgo ? <FundedAge>{fundingAgo}</FundedAge> : undefined}
          </ValueContainer>
        </Row>
      ) : undefined}
    </Root>
  )
}

const Root = styled(StatsPanel)`
  display: flex;
  flex-direction: column;
  gap: 6px;

  font-size: 12px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 12px;
`

const Key = styled.span`
  color: var(--text-10);
`

const ValueContainer = styled.div`
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  gap: 2px;

  color: var(--text-11);
  font-weight: 600;
`

const FundedAge = styled.span`
  color: var(--text-11);
  font-weight: 600;
`

const PlatformLabel = styled.div`
  color: var(--text-12);
  font-weight: 600;
  margin-inline-end: 2px;

  display: grid;
  grid-auto-flow: column;
  gap: 2px;
  align-items: center;
`
